import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Reveal from '@/components/feature/Reveal';
import useSeo from '@/hooks/useSeo';

export default function About() {
  useSeo({
    title: '关于我们 - 美国伦理片内容与编辑原则',
    description:
      '了解美国伦理片网站的宗旨、内容范围与编辑原则，我们专注收录与介绍美国伦理题材电影，为影迷提供剧情解析、专业影评与精选推荐。',
    keywords: '美国伦理片,关于我们,伦理电影网站,美国伦理电影介绍',
  });

  return (
    <Layout>
      <section className="px-4 md:px-8 pt-10 pb-8 border-b border-background-200">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">关于我们</span>
        </nav>
        <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4">关于美国伦理片</h1>
        <p className="text-sm md:text-base leading-8 text-foreground-600 max-w-3xl">
          一个认真对待伦理题材的电影资料库。我们相信，伦理电影最珍贵的价值，是它愿意把观众放进一个无法轻易站队的处境里。
        </p>
      </section>

      <div className="px-4 md:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8 min-w-0">
            <Reveal>
              <article className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
                <h2 className="text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-flag-2-line text-primary-500" /> 网站宗旨
                </h2>
                <p className="text-sm md:text-base leading-8 text-foreground-700 mb-4">
                  本站以专业、克制的姿态，收录并介绍美国伦理题材电影。我们不追求情节的猎奇，而是关注作品如何在道德的灰色地带里，逼问人物的选择与代价。我们希望为影迷、研究者与评论爱好者，提供一个可以安静阅读、深入思考的内容空间。
                </p>
                <p className="text-sm md:text-base leading-8 text-foreground-700">
                  无论你是想快速了解一部影片讲了什么，还是希望读到更深入的剧情解析与影评，这里都为你准备了清晰的入口。
                </p>
              </article>
            </Reveal>

            <Reveal delay={80}>
              <article className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
                <h2 className="text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-stack-line text-primary-500" /> 内容范围
                </h2>
                <ul className="space-y-3 text-sm md:text-base leading-8 text-foreground-700">
                  <li className="flex gap-3">
                    <i className="ri-checkbox-circle-line text-accent-500 mt-1.5" />
                    <span><strong className="text-foreground-900">影片收录</strong>：整理美国伦理题材电影的剧情简介、主创团队与上映资料。</span>
                  </li>
                  <li className="flex gap-3">
                    <i className="ri-checkbox-circle-line text-accent-500 mt-1.5" />
                    <span><strong className="text-foreground-900">专业影评</strong>：由编辑部撰写深度解读，帮助读者理解作品背后的伦理命题。</span>
                  </li>
                  <li className="flex gap-3">
                    <i className="ri-checkbox-circle-line text-accent-500 mt-1.5" />
                    <span><strong className="text-foreground-900">专题策划</strong>：围绕道德困境、家庭肖像等主题，组织片单与系列评论。</span>
                  </li>
                  <li className="flex gap-3">
                    <i className="ri-checkbox-circle-line text-accent-500 mt-1.5" />
                    <span><strong className="text-foreground-900">分类检索</strong>：支持按类型、年份、评分区间筛选，快速定位想看的内容。</span>
                  </li>
                </ul>
              </article>
            </Reveal>

            <Reveal delay={120}>
              <article className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
                <h2 className="text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-shield-check-line text-primary-500" /> 编辑原则
                </h2>
                <p className="text-sm md:text-base leading-8 text-foreground-700 mb-4">
                  我们坚持「理解优先于评判」。在介绍每一部影片时，我们尽可能呈现不同角色的立场与处境，而不是急于给出道德结论。评论可以带有观点，但必须建立在准确的内容之上。
                </p>
                <p className="text-sm md:text-base leading-8 text-foreground-700">
                  本站所有影片信息、评分与影评均为演示内容，用于展示页面的结构与阅读体验。
                </p>
              </article>
            </Reveal>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-6">
                <h2 className="text-base font-semibold text-secondary-900 mb-4 flex items-center gap-2">
                  <i className="ri-mail-send-line" /> 联系我们
                </h2>
                <p className="text-sm leading-7 text-secondary-900/80 mb-3">
                  如对内容有建议、勘误或合作意向，欢迎通过邮箱与我们联系。为避免垃圾邮件，邮箱以文字形式展示：
                </p>
                <p className="text-sm font-medium text-foreground-900 bg-background-50 rounded-md border border-secondary-200 px-3 py-2.5 select-all">
                  feedback [at] ethfilms-demo [dot] com
                </p>
                <p className="mt-3 text-xs text-secondary-800/70">我们会在 3 个工作日内回复来信。</p>
              </div>

              <div className="rounded-lg bg-accent-50 border border-accent-200 p-6">
                <h2 className="text-base font-semibold text-accent-900 mb-3 flex items-center gap-2">
                  <i className="ri-error-warning-line" /> 免责声明
                </h2>
                <p className="text-xs leading-6 text-accent-900/80">
                  本站为内容展示型网站，所有数据均为演示用途，不提供任何影片的在线播放、下载或资源分享服务，也不代表任何版权方立场。如本站内容涉及权益问题，欢迎来信说明。
                </p>
              </div>

              <div className="rounded-lg bg-background-50 border border-background-200 p-6">
                <h2 className="text-base font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-copyright-line" /> 版权信息
                </h2>
                <p className="text-xs leading-6 text-foreground-600">
                  © 2026 美国伦理片 · 保留所有权利。本站原创文字内容未经授权请勿转载，演示数据版权归原作者所有。
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}